<script setup lang="ts">
    import { computed, onMounted, watch, nextTick, Ref, shallowReactive } from 'vue'
    import { useRoute } from 'vue-router'

    import CorporateNavigation from '@common/Components/CorporateNavigation.vue'
    import ProjectsGrid from '@Quest/Projects/Components/ProjectsGrid.vue'

    import type { FinishedProjectsPageReadModel } from '@Quest/Strategies/readmodels'
    import { ProjectsGridViewModel } from "@Quest/Projects/Components/ProjectsGrid"

    import { useFinishedProjectsPageQueryV1 } from '@Quest/Strategies/queries-vue-composables'
    import { useSaveViewFinishedProjectsPageCommandV1 } from '@Quest/Strategies/commands-vue-composables'

    const route = useRoute()

    const workspaceId = computed(() => Number(route.params.workspaceId ?? 0))
    const strategyId = computed(() => String(route.params.strategyId ?? ''))

    const grid = shallowReactive(new ProjectsGridViewModel())

    const {
        runFinishedProjectsPageQueryV1,
        modelFinishedProjectsPageQueryV1
    } = useFinishedProjectsPageQueryV1(workspaceId, strategyId)

    const rm = modelFinishedProjectsPageQueryV1 as Ref<FinishedProjectsPageReadModel | null>

    const {
        invokeSaveViewFinishedProjectsPageCommandV1,
        succeedSaveViewFinishedProjectsPageCommandV1
    } = useSaveViewFinishedProjectsPageCommandV1(workspaceId, strategyId)

    watch(
        rm,
        async (model) =>
        {
            if (!model) return

            await nextTick()

            grid.renderGrid(
                model.Grid.IdField,
                model.Grid.Fields,
                model.Grid.DefaultFieldIds,
                model.Grid.Options
            )

            grid.setProjects(model.Projects.map(p => p.Properties))
        },
        { immediate: true }
    )

    watch(succeedSaveViewFinishedProjectsPageCommandV1, (current, previous) =>
    {
        if (current && !previous)
        {
            alert("Saved")
        }
    })

    function handleShowGridColumnsEditor()
    {
        grid.showGridColumnsEditor()
    }

    function handleSaveViewSettings()
    {
        const json = grid.getJson()
        invokeSaveViewFinishedProjectsPageCommandV1({ grid: json })
    }

    function handleClearGridFilters()
    {
        grid.clearFilters()
    }

    function handleExportGrid()
    {
        grid.exportGrid()
    }

    onMounted(() =>
    {
        document.title = "Enterprise Finished | TransparentChoice"
        runFinishedProjectsPageQueryV1()
    })
</script>

<template>
    <div>
        <CorporateNavigation />

        <div class="container-fluid" style="margin-top: 80px; padding-top: 40px;">
            <div class="row">
                <div class="col-lg-6">
                    <h1 style="font-size: 2em; font-weight: 300; color: #33475b; margin: 0;">Finished projects</h1>
                    <h6 class="text-muted mt-2">Enterprise Portfolio</h6>
                </div>
                <div class="col-lg-6 text-right align-self-end">
                    <button role="button" class="btn btn-outline-secondary" style="padding: 0.7em 2em;" @click="handleSaveViewSettings">Save</button>
                </div>
            </div>

            <hr style="border-color: #bbb; margin: 20px 0 60px;" />

            <div class="toolbar">
                <div class="toolbar-left">
                    <!--<button type="button" class="btn btn-outline-secondary">Action ({{selectedIds.length}})</button>-->
                </div>
                <div class="toolbar-right">
                    <button type="button" class="btn btn-outline-secondary" @click="handleShowGridColumnsEditor">Edit columns</button>

                    <div class="btn-group">
                        <button type="button" class="btn btn-outline-secondary dropdown-toggle" data-toggle="dropdown">
                            Actions
                        </button>
                        <div class="dropdown-menu dropdown-menu-right">
                            <a class="dropdown-item" @click="handleClearGridFilters">Clear Filters</a>
                        </div>
                    </div>

                    <button type="button" class="btn btn-outline-secondary" @click="handleExportGrid" title="Download to spreadsheet">
                        <i class="fa fa-download fa-lg"></i>
                    </button>
                </div>
            </div>

            <ProjectsGrid :viewModel="grid" />

        </div>
    </div>
</template>

<style scoped>
    .toolbar {
        background-color: #f8f8f8; /* Light gray background for the toolbar */
        padding: 10px 15px; /* Padding around the toolbar */
        border: 1px solid #e4e4e4; /* A light border to give separation */
        display: flex; /* Use flex to give layout flexibility */
        align-items: center; /* Center-align items vertically */
        justify-content: space-between; /* Separate items */
    }

    .toolbar-left,
    .toolbar-right {
        display: flex;
        align-items: center;
        gap: 8px;
        flex-wrap: wrap;
    }
</style>